import React, { createContext, ReactNode, useContext, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import type { CodeSample } from "../shared/types";
import remarkDocs from "./remarkDocs";
import { CodeBlock, CodeGroup } from "../components/code";
import { Callout, CalloutRole, Card, Cards, Steps, Tab, Tabs } from "../components/blocks";
import { DocsLink } from "../components/DocsLink";

/**
 * A Markdown page body: `react-markdown` with GFM, directives and the docs' component mapping
 * (`remarkDocs`). Code blocks show the build's highlighted HTML (`code`, matched by their text), so
 * the browser runs no highlighter.
 */

const CodeContext = createContext<Map<string, CodeSample>>(new Map());

const textOf = (children: ReactNode): string =>
  React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : React.isValidElement(child) ? textOf((child.props as { children?: ReactNode }).children) : ""))
    .join("");

/** A fenced block → the build's highlighted sample; inline code stays inline. */
const MarkdownCode = ({ inline, children }: { inline?: boolean; children?: ReactNode }) => {
  const samples = useContext(CodeContext);
  if (inline) return <code className="rounded bg-muted px-1 py-0.5 text-bodySmall">{children}</code>;
  const text = textOf(children).replace(/\n$/, "");
  const sample = samples.get(text);

  return sample ? (
    <CodeBlock sample={sample} />
  ) : (
    <pre className="my-4 overflow-x-auto rounded-lg border border-border bg-card p-4 text-bodySmall">
      <code>{text}</code>
    </pre>
  );
};

/** A `::::code-group`: its fenced blocks as one tab group (labels from each block's meta / language). */
const MarkdownCodeGroup = ({ children }: { children?: ReactNode }) => {
  const samples = useContext(CodeContext);
  const blocks = React.Children.toArray(children)
    .map((child) => samples.get(textOf(child).replace(/\n$/, "")))
    .filter((s): s is CodeSample => Boolean(s));

  return <CodeGroup samples={blocks} />;
};

type DirectiveProps = { children?: ReactNode; role?: string; title?: string; href?: string };

const components = {
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <DocsLink href={href} className="text-purpleGreen underline underline-offset-2">
      {children}
    </DocsLink>
  ),
  pre: ({ children }: { children?: ReactNode }) => <>{children}</>,
  code: MarkdownCode,
  blockquote: ({ children }: { children?: ReactNode }) => <Callout role="note">{children}</Callout>,
  table: ({ children }: { children?: ReactNode }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-left text-bodySmall [&_td]:border-b [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-2">
        {children}
      </table>
    </div>
  ),
  "docs-callout": ({ role, title, children }: DirectiveProps) => (
    <Callout role={(role as CalloutRole) ?? "note"} title={title}>
      {children}
    </Callout>
  ),
  "docs-steps": ({ children }: DirectiveProps) => <Steps>{children}</Steps>,
  "docs-cards": ({ children }: DirectiveProps) => <Cards>{children}</Cards>,
  "docs-card": ({ title, href, children }: DirectiveProps) => (
    <Card title={title} href={href}>
      {children}
    </Card>
  ),
  "docs-code-group": MarkdownCodeGroup,
  "docs-tabs": ({ children }: DirectiveProps) => <Tabs>{children}</Tabs>,
  "docs-tab": ({ title, children }: DirectiveProps) => <Tab title={title}>{children}</Tab>,
  // A panel step (**Panel › Developers › Keys**): the app's own route, outside the docs.
  "docs-panel-link": ({ href, children }: DirectiveProps) => (
    <a href={href} className="font-semibold text-purpleGreen underline underline-offset-2">
      {children}
    </a>
  ),
};

export const PROSE =
  "text-bodyMedium text-foreground [&_h2]:mt-10 [&_h2]:scroll-mt-24 [&_h2]:text-head4 [&_h2]:font-semibold [&_h3]:mt-8 [&_h3]:scroll-mt-24 [&_h3]:text-head5 [&_h3]:font-semibold [&_h4]:mt-6 [&_h4]:font-semibold [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-3 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6";

export const Markdown = ({ markdown, code }: { markdown: string; code: CodeSample[] }) => {
  const samples = useMemo(() => new Map(code.map((sample) => [sample.code, sample])), [code]);

  return (
    <CodeContext.Provider value={samples}>
      <div className={PROSE}>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkDirective, remarkDocs]} components={components as never}>
          {markdown}
        </ReactMarkdown>
      </div>
    </CodeContext.Provider>
  );
};
